function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;
  const summary = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  summary.setAttribute('aria-expanded', false);
  summary.focus();
}

class DiscountSort {
  static get SORT_DESC() {
    return 'discount-descending';
  }

  static get SORT_ASC() {
    return 'discount-ascending';
  }

  static isDiscountSortValue(value) {
    return value === this.SORT_DESC || value === this.SORT_ASC;
  }

  static getActiveDirection() {
    const params = new URLSearchParams(window.location.search);
    const fromSortBy = this.directionFromSortValue(params.get('sort_by'));
    if (fromSortBy) return fromSortBy;

    const legacy = params.get('sort_discount');
    return legacy === 'asc' || legacy === 'desc' ? legacy : null;
  }

  static stripForServerFetch(searchParams) {
    const params = new URLSearchParams(searchParams);
    if (this.isDiscountSortValue(params.get('sort_by'))) params.delete('sort_by');
    params.delete('sort_discount');
    return params.toString();
  }

  static normalizeUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const sortBy = params.get('sort_by');
    const legacy = params.get('sort_discount');

    if (this.isDiscountSortValue(sortBy)) {
      if (legacy) params.delete('sort_discount');
    } else if (legacy === 'asc' || legacy === 'desc') {
      params.delete('sort_discount');
      params.set('sort_by', this.sortValueFromDirection(legacy));
    } else {
      return;
    }

    const searchParams = params.toString();
    const newUrl = searchParams
      ? `${window.location.pathname}?${searchParams}`
      : window.location.pathname;

    window.history.replaceState({ searchParams }, '', newUrl);
    FacetFiltersForm.searchParamsInitial = searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
  }

  static directionFromSortValue(value) {
    if (value === this.SORT_ASC) return 'asc';
    if (value === this.SORT_DESC) return 'desc';
    return null;
  }

  static sortValueFromDirection(direction) {
    return direction === 'asc' ? this.SORT_ASC : this.SORT_DESC;
  }

  static sortGrid(direction) {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const items = Array.from(grid.children).filter((element) => element.classList.contains('grid__item'));
    if (items.length < 2) return;

    const multiplier = direction === 'asc' ? 1 : -1;
    items.sort((a, b) => {
      const discountA = Number(a.dataset.discount) || 0;
      const discountB = Number(b.dataset.discount) || 0;
      return (discountA - discountB) * multiplier;
    });

    items.forEach((item) => grid.appendChild(item));
  }

  static applyIfActive() {
    const direction = this.getActiveDirection();
    if (direction) this.sortGrid(direction);
  }

  static syncSelects(sortValue) {
    ['SortBy', 'SortBy-mobile'].forEach((id) => {
      const select = document.getElementById(id);
      if (select) select.value = sortValue;
    });
  }

  static initFromUrl() {
    this.normalizeUrlParams();
    const direction = this.getActiveDirection();
    if (!direction) return;
    this.syncSelects(this.sortValueFromDirection(direction));
    this.sortGrid(direction);
  }

  static mergeIntoSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const sortBy = params.get('sort_by');

    if (this.isDiscountSortValue(sortBy)) {
      params.delete('sort_discount');
      return params.toString();
    }

    if (sortBy) {
      params.delete('sort_discount');
      return params.toString();
    }

    const activeDirection = this.getActiveDirection();
    if (activeDirection) {
      params.set('sort_by', this.sortValueFromDirection(activeDirection));
      params.delete('sort_discount');
    }

    return params.toString();
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionEl = productGridContainer && productGridContainer.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');

    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    const fetchParams = DiscountSort.stripForServerFetch(searchParams);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${fetchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;

    target.innerHTML = source.innerHTML;

    target.querySelector('.collection')?.classList.remove('loading');

    target.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    DiscountSort.applyIfActive();
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsed.getElementById('ProductCount');
    const count = sourceCount ? sourceCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static getOpenDetailIds(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll('details[open]')).map((detail) => detail.id);
  }

  static syncFacetWrapper(target, source, openDetailIds) {
    if (!target || !source) return;

    const sourceFilters = source.querySelectorAll('.js-filter');
    const targetFilters = target.querySelectorAll('.js-filter');
    const sourceEmpty = source.innerHTML.trim() === '' || sourceFilters.length === 0;

    if (targetFilters.length > 0 && sourceEmpty) return;

    target.innerHTML = source.innerHTML;
    openDetailIds.forEach((id) => {
      const detail = target.querySelector(`#${CSS.escape(id)}`);
      if (detail) detail.setAttribute('open', '');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = html instanceof Document ? html : new DOMParser().parseFromString(html, 'text/html');

    const desktopTarget = document.getElementById('FacetsWrapperDesktop');
    const desktopSource = parsedHTML.getElementById('FacetsWrapperDesktop');
    const desktopOpenIds = FacetFiltersForm.getOpenDetailIds(desktopTarget);
    FacetFiltersForm.syncFacetWrapper(desktopTarget, desktopSource, desktopOpenIds);

    const mobileTarget = document.getElementById('FacetsWrapperMobile');
    const mobileSource = parsedHTML.getElementById('FacetsWrapperMobile');
    const mobileOpenIds = FacetFiltersForm.getOpenDetailIds(mobileTarget);
    FacetFiltersForm.syncFacetWrapper(mobileTarget, mobileSource, mobileOpenIds);

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (event && event.target) {
      const activeFilter = event.target.closest('.js-filter');
      if (activeFilter) {
        const focusSelector = activeFilter.classList.contains('mobile-facets__details')
          ? '.mobile-facets__summary'
          : '.facets__summary';
        const isTextInput = event.target.getAttribute('type') === 'text';
        const focusTarget = activeFilter.querySelector(focusSelector);
        if (focusTarget && !isTextInput) focusTarget.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const parsedHTML = html instanceof Document ? html : new DOMParser().parseFromString(html, 'text/html');

    ['.active-facets-desktop', '.active-facets-mobile', '.active-facets-drawer'].forEach((selector) => {
      const source = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);
      if (!target) return;
      target.innerHTML = source ? source.innerHTML : '';
    });

    const drawerSummarySource = parsedHTML.getElementById('MobileFacetsActiveSummary');
    const drawerSummaryTarget = document.getElementById('MobileFacetsActiveSummary');
    if (drawerSummarySource && drawerSummaryTarget) {
      drawerSummaryTarget.innerHTML = drawerSummarySource.innerHTML;
    }

    const mobilePillsSource = parsedHTML.getElementById('MobileFacetsPills');
    const mobilePillsTarget = document.getElementById('MobileFacetsPills');
    if (mobilePillsSource && mobilePillsTarget) {
      mobilePillsTarget.innerHTML = mobilePillsSource.innerHTML;
    }

    FacetFiltersForm.toggleActiveFacets(false);
    DiscountSort.initFromUrl();
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }

    FacetFiltersForm.initSortSelects();
  }

  static initSortSelects() {
    const selects = document.querySelectorAll(
      '#FacetSortForm [data-select-input], #FacetSortDrawerForm [data-select-input]'
    );

    if (!selects.length) return;

    selects.forEach((select) => {
      select.classList.remove('hidden');
    });

    if (typeof slate !== 'undefined' && slate.utils && typeof slate.utils.resizeSelects === 'function') {
      slate.utils.resizeSelects(Array.from(selects));
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!target) return;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    if (!productGrid || !productGrid.dataset.id) return [];
    return [{ section: productGrid.dataset.id }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const sortBy = formData.get('sort_by');
    if (DiscountSort.isDiscountSortValue(sortBy)) formData.delete('sort_by');
    const params = new URLSearchParams(formData);
    const current = new URLSearchParams(window.location.search);

    ['q', 'options[prefix]', 'type'].forEach((key) => {
      if (!params.has(key) && current.has(key)) {
        params.set(key, current.get(key));
      }
    });

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(DiscountSort.mergeIntoSearchParams(searchParams), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const currentForm = event.target.closest('form');
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    if (event.srcElement.className === 'mobile-facets__checkbox') {
      this.onSubmitForm(this.createSearchParams(currentForm), event);
      return;
    }

    if (currentForm && (currentForm.id === 'FacetSortForm' || currentForm.id === 'FacetSortDrawerForm')) {
      const sortBy = new FormData(currentForm).get('sort_by');

      if (DiscountSort.isDiscountSortValue(sortBy)) {
        const direction = DiscountSort.directionFromSortValue(sortBy);
        DiscountSort.syncSelects(sortBy);

        const params = new URLSearchParams(window.location.search);
        params.set('sort_by', sortBy);
        params.delete('sort_discount');
        const searchParams = params.toString();

        FacetFiltersForm.searchParamsPrev = searchParams;
        FacetFiltersForm.updateURLHash(searchParams);
        DiscountSort.sortGrid(direction);
        return;
      }

      DiscountSort.syncSelects(sortBy);
    }

    const forms = [];
    const isMobile =
      currentForm?.id === 'FacetFiltersFormMobile' || currentForm?.id === 'FacetSortDrawerForm';

    sortFilterForms.forEach((form) => {
      if (!isMobile) {
        if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm') {
          forms.push(this.createSearchParams(form));
        }
      } else if (form.id === 'FacetFiltersFormMobile' || form.id === 'FacetSortDrawerForm') {
        const formData = new FormData(form);
        if (formData.get('sort_by') !== '') forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    url = DiscountSort.mergeIntoSearchParams(url);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

document.addEventListener('DOMContentLoaded', () => {
  DiscountSort.initFromUrl();
  FacetFiltersForm.initSortSelects();
});

class PriceRange extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.mode = this.dataset.rangeMode || 'price';
    this.rangeMin = Number(this.dataset.rangeMin || 0);
    this.rangeMax = Number(this.dataset.rangeMax || 0);
    this.subunit = Number(this.dataset.subunit || 100);
    this.currency = this.dataset.currency || 'COP';
    this.valueSuffix = this.dataset.valueSuffix || '';

    this.minInput = this.querySelector('.price-range__input-min');
    this.maxInput = this.querySelector('.price-range__input-max');
    this.minRange = this.querySelector('.price-range__range-min');
    this.maxRange = this.querySelector('.price-range__range-max');
    this.trackActive = this.querySelector('.price-range__track-active');
    this.labelMin = this.querySelector('.price-range__label-min');
    this.labelMax = this.querySelector('.price-range__label-max');
    this.rangeCheckboxes = this.querySelectorAll('[data-range-checkbox]');

    if (!this.minInput || !this.maxInput || !this.minRange || !this.maxRange) return;

    this.minGap =
      this.mode === 'list' ? 1 : Math.max(this.subunit, Math.round(this.rangeMax * 0.01));

    this.onInputChange = this.onInputChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onMinSliderInput = () => this.onSliderInput('min');
    this.onMaxSliderInput = () => this.onSliderInput('max');

    this.minInput.addEventListener('change', this.onInputChange);
    this.maxInput.addEventListener('change', this.onInputChange);
    this.minInput.addEventListener('keydown', this.onKeyDown);
    this.maxInput.addEventListener('keydown', this.onKeyDown);
    this.minRange.addEventListener('input', this.onMinSliderInput);
    this.maxRange.addEventListener('input', this.onMaxSliderInput);
    this.minRange.addEventListener('change', () => this.triggerFormInput());
    this.maxRange.addEventListener('change', () => this.triggerFormInput());

    this.syncFromInputs(false);
    this.updateUI();
  }

  isListMode() {
    return this.mode === 'list';
  }

  toSubunit(displayValue) {
    return Math.round(Number(displayValue) * this.subunit);
  }

  toDisplay(value) {
    if (this.isListMode()) return Math.round(Number(value));
    return Math.round(Number(value) / this.subunit);
  }

  parseInputValue(rawValue, fallback) {
    if (rawValue === '') return fallback;
    if (this.isListMode()) return Number(rawValue);
    return this.toSubunit(rawValue);
  }

  onSliderInput(source) {
    let minVal = Number(this.minRange.value);
    let maxVal = Number(this.maxRange.value);

    if (minVal > maxVal - this.minGap) {
      if (source === 'min') {
        minVal = maxVal - this.minGap;
        this.minRange.value = minVal;
      } else {
        maxVal = minVal + this.minGap;
        this.maxRange.value = maxVal;
      }
    }

    this.setInputValues(minVal, maxVal);
    this.updateUI();
    this.triggerFormInput();
  }

  onInputChange() {
    this.syncFromInputs(true);
    this.updateUI();
    this.triggerFormInput();
  }

  syncFromInputs(validate) {
    let minVal = this.parseInputValue(this.minInput.value, this.rangeMin);
    let maxVal = this.parseInputValue(this.maxInput.value, this.rangeMax);

    if (validate) {
      minVal = Math.max(this.rangeMin, Math.min(minVal, this.rangeMax));
      maxVal = Math.max(this.rangeMin, Math.min(maxVal, this.rangeMax));
      if (minVal > maxVal - this.minGap) {
        minVal = maxVal - this.minGap;
      }
    }

    this.minRange.value = minVal;
    this.maxRange.value = maxVal;
    this.setInputValues(minVal, maxVal);
  }

  setInputValues(minVal, maxVal) {
    this.minInput.value = this.toDisplay(minVal);
    this.maxInput.value = this.toDisplay(maxVal);
  }

  updateUI() {
    const minVal = Number(this.minRange.value);
    const maxVal = Number(this.maxRange.value);
    const range = this.rangeMax - this.rangeMin || 1;
    const left = ((minVal - this.rangeMin) / range) * 100;
    const width = ((maxVal - minVal) / range) * 100;

    if (this.trackActive) {
      this.trackActive.style.left = `${left}%`;
      this.trackActive.style.width = `${width}%`;
    }

    if (this.labelMin) this.labelMin.textContent = this.formatValue(minVal);
    if (this.labelMax) this.labelMax.textContent = this.formatValue(maxVal);

    if (this.isListMode()) {
      this.updateListCheckboxes(minVal, maxVal);
    }
  }

  updateListCheckboxes(minVal, maxVal) {
    const isFullRange = minVal <= this.rangeMin && maxVal >= this.rangeMax;

    this.rangeCheckboxes.forEach((checkbox) => {
      const value = Number(checkbox.value);
      const shouldCheck = !isFullRange && value >= minVal && value <= maxVal;
      checkbox.checked = shouldCheck;
    });
  }

  formatValue(value) {
    const num = this.toDisplay(value);

    if (this.isListMode()) {
      return `${num}${this.valueSuffix}`;
    }

    try {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: this.currency,
        maximumFractionDigits: 0,
      }).format(num);
    } catch (e) {
      return '$' + num.toLocaleString('es-CO');
    }
  }

  triggerFormInput() {
    const minVal = Number(this.minRange.value);
    const maxVal = Number(this.maxRange.value);

    if (this.isListMode()) {
      this.updateListCheckboxes(minVal, maxVal);
      const form = this.closest('form');
      if (form) form.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    this.minInput.value = minVal === this.rangeMin ? '' : minVal;
    this.maxInput.value = maxVal === this.rangeMax ? '' : maxVal;

    const form = this.closest('form');
    if (form) form.dispatchEvent(new Event('input', { bubbles: true }));

    this.setInputValues(minVal, maxVal);
  }

  disconnectedCallback() {
    this.initialized = false;
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    event.stopPropagation();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (form) form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class CollectionLoadMore extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const button = event.target.closest('.collection-load-more__button');
    if (!button || this.loading) return;
    this.loadNextPage(button);
  }

  loadNextPage(button) {
    const nextUrl = button.dataset.nextUrl;
    const grid = document.getElementById('product-grid');
    if (!nextUrl || !grid || !grid.dataset.id) return Promise.resolve(false);

    this.loading = true;
    button.disabled = true;
    button.classList.add('loading');
    const spinner = button.querySelector('.collection-load-more__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const fetchUrl = `${nextUrl}${nextUrl.includes('?') ? '&' : '?'}section_id=${grid.dataset.id}`;

    return fetch(fetchUrl)
      .then((response) => response.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const newItems = parsed.querySelectorAll('#product-grid > .grid__item, #product-grid > .list-view-item');
        const countBefore = grid.querySelectorAll(':scope > .grid__item, :scope > .list-view-item').length;

        newItems.forEach((item) => grid.appendChild(item));

        if (newItems.length === 0) return false;

        DiscountSort.applyIfActive();

        const newLoadMore = parsed.querySelector('collection-load-more');
        if (newLoadMore && newLoadMore.innerHTML.trim()) {
          this.innerHTML = newLoadMore.innerHTML;
        } else {
          this.remove();
        }

        if (window.lazySizes && typeof window.lazySizes.loader !== 'undefined') {
          window.lazySizes.loader.checkElems();
        }

        document.dispatchEvent(new CustomEvent('collection:products:loaded'));

        return grid.querySelectorAll(':scope > .grid__item, :scope > .list-view-item').length > countBefore;
      })
      .catch(() => {
        button.disabled = false;
        button.classList.remove('loading');
        const spinnerEl = button.querySelector('.collection-load-more__spinner');
        if (spinnerEl) spinnerEl.classList.add('hidden');
        return false;
      })
      .finally(() => {
        this.loading = false;
      });
  }
}

customElements.define('collection-load-more', CollectionLoadMore);

class CollectionScrollRestore {
  static STORAGE_KEY = 'kronotime:collection-view';
  static MAX_AGE_MS = 30 * 60 * 1000;

  static getCurrentUrl() {
    return window.location.pathname + window.location.search;
  }

  static getProductCount(grid) {
    return grid.querySelectorAll(':scope > .grid__item, :scope > .list-view-item').length;
  }

  static save() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const state = {
      url: this.getCurrentUrl(),
      scrollY: window.scrollY,
      productCount: this.getProductCount(grid),
      timestamp: Date.now(),
    };

    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  static getSavedState() {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;

      const state = JSON.parse(raw);
      if (Date.now() - state.timestamp > this.MAX_AGE_MS) return null;
      if (state.url !== this.getCurrentUrl()) return null;
      if (!state.productCount || state.productCount < 1) return null;

      return state;
    } catch {
      return null;
    }
  }

  static isBackNavigation() {
    const nav = performance.getEntriesByType('navigation')[0];
    return Boolean(nav && nav.type === 'back_forward');
  }

  static init() {
    if (!document.getElementById('product-grid')) return;

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('#product-grid a[href*="/products/"]')) return;
        this.save();
      },
      true
    );

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const state = this.getSavedState();
      if (!state) return;
      requestAnimationFrame(() => window.scrollTo(0, state.scrollY));
    });

    document.addEventListener('DOMContentLoaded', () => {
      this.restore();
    });
  }

  static async restore() {
    if (!this.isBackNavigation()) return;

    const state = this.getSavedState();
    if (!state) return;

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    const initialCount = this.getProductCount(grid);
    if (initialCount >= state.productCount) {
      requestAnimationFrame(() => window.scrollTo(0, state.scrollY));
      return;
    }

    document.documentElement.classList.add('collection-scroll-restoring');

    try {
      while (this.getProductCount(grid) < state.productCount) {
        const loadMore = document.querySelector('collection-load-more');
        const button = loadMore?.querySelector('.collection-load-more__button');
        if (!button || loadMore.loading) break;

        const loaded = await loadMore.loadNextPage(button);
        if (!loaded) break;
      }

      DiscountSort.applyIfActive();
    } finally {
      document.documentElement.classList.remove('collection-scroll-restoring');
      requestAnimationFrame(() => window.scrollTo(0, state.scrollY));
    }
  }
}

CollectionScrollRestore.init();
